
export class DagTraveller<T> {
    graph: DirectedAcyclicGraph<T>;
    currentNode: DagNode<T>;

    travelled: DagNode<T>[] = [];
    branchesTaken: number[] = [];

    constructor(graph: DirectedAcyclicGraph<T>) {
        this.graph = graph;
        this.currentNode = graph.root;
    }

    goToNode(predicate: (nodeValue: T) => boolean, branchPaths: number[]): DagNode<T>|null {
        let node = this.graph.root;
        let travelled = [node];
        let branchesTaken = [];
        let i = 0;
        
        while (node) {
            if (predicate(node.value))
                break;

            if (node.edges.length === 0) return null;
            if (node.edges.length === 1) {
                node = node.edges[0];
            }
            else {
                let branchIndex = branchPaths[i];
                node = node.edges[branchIndex];
                branchesTaken.push(branchIndex);
                i += 1;
            }

            travelled.push(node);
        }

        travelled.pop();
        this.currentNode = node;
        this.travelled = travelled;
        this.branchesTaken = branchesTaken;
        return node;
    }

    get isBranch(): boolean {
        return this.currentNode.edges.length > 1;
    }

    get atEnd(): boolean {
        return this.currentNode.edges.length === 0;
    }

    get atRoot(): boolean {
        return this.currentNode === this.graph.root;
    }

    goToNext(edgeIndex: number): DagNode<T> {
        if (this.isBranch) {
            this.branchesTaken.push(edgeIndex);
        }
        
        this.travelled.push(this.currentNode);
        this.currentNode = this.currentNode.edges[edgeIndex];
        return this.currentNode;
    }

    goToNextWith(predicate: (nodeValue: T) => boolean): DagNode<T>|null {
        for (let i = 0; i < this.currentNode.edges.length; i++) {
            if (predicate(this.currentNode.edges[i].value)) {
                if (this.isBranch)
                    this.branchesTaken.push(i);
                this.travelled.push(this.currentNode);
                this.currentNode = this.currentNode.edges[i];
                return this.currentNode;
            }
        }

        return null;
    }

    goBack(): DagNode<T>|null {
        let node = this.travelled.pop();
        if (node == null)
            return null;
        this.currentNode = node;
        if (this.isBranch) this.branchesTaken.pop();
        return this.currentNode;
    }

    goToRoot(): DagNode<T> {
        this.currentNode = this.graph.root;
        this.travelled = [];
        this.branchesTaken = [];
        return this.currentNode;
    }

    get slideNumber(): number {
        return this.travelled.length;
    }

    get progress(): number {
        return this.currentNode.maxLengthToRoot / this.graph.maxLength;
    }
}

export class DirectedAcyclicGraph<T> {
    root: DagNode<T>;
    private nodes: DagNode<T>[] = [];
    private _maxLength: number;

    get maxLength(): number {
        if (this._maxLength == null) {
            this._maxLength = this.maxLengthsRec(this.root, 0);
        }

        return this._maxLength;
    }

    createNode(value: T, connectFrom: DagNode<T>[]): DagNode<T> {
        let node = new DagNode<T>(value);

        for (let n of connectFrom) {
            n.addConnection(node);
        }

        this.nodes.push(node);
        return node;
    }

    size() {
        return this.nodes.length;
    }

    updateMaxLengths() {
        this.maxLengthsRec(this.root, 0);
    }

    private maxLengthsRec(node: DagNode<T>, dist: number): number {
        if (dist < node.maxLengthToRoot)
            return node.maxLengthToRoot;

        let max = dist;
        node.maxLengthToRoot = dist;

        for (let n of node.edges) {
            let d = this.maxLengthsRec(n, dist + 1);
            max = d > max ? d : max;
        }

        node.maxLengthToEnd = max;
        return max;
    }
}

export class DagNode<T> {
    value: T;
    edges: DagNode<T>[] = [];
    maxLengthToRoot: number = 0;
    maxLengthToEnd: number = 0;
    private connectionsTo: number = 0;

    constructor(value: T) {
        this.value = value;
    }

    addConnection(node: DagNode<T>) {
        this.edges.push(node);
        node.connectionsTo += 1;
    }

    numberOfConnections() {
        return this.edges.length;
    }

    getConnectionsTo() {
        return this.connectionsTo;
    }
}